import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import TaxSnapshotWidget from '../components/TaxSnapshot/TaxSnapshotWidget';
import DeductionGapsPanel from '../components/Deductions/DeductionGapsPanel';
import SetupPreviewCard from '../components/onboarding/SetupPreviewCard';
import SavingsProgressTracker from '../components/onboarding/SavingsProgressTracker';
import CpaPacketCard from '../components/CpaPacketCard';
import { StepGuide, useOnboarding } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calculator,
  Calendar,
  FileText,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle,
  ArrowRight,
  PiggyBank,
  Receipt,
  Building,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Lightbulb,
  Target,
  Percent,
  Home,
  Car,
  Sparkles,
  Plane,
  UtensilsCrossed,
  Zap,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PEER_BENCHMARKS removed — moved to backend dashboard.py _PEER_BENCHMARKS (Commandment II)
// Backend returns summary.peer_benchmark with peer_avg, optimization_pct, gap, etc.

// Axis labels: $850, $1.5k, $12k (avoids "$0k $0k $1k" for small values)
const formatAxisMoney = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1000) return `$${(n / 1000).toFixed(abs >= 10000 || abs % 1000 === 0 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { triggerAction, onboardingData } = useOnboarding();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [topDeductions, setTopDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [taxLiability, setTaxLiability] = useState(null);
  const [yearOverYear, setYearOverYear] = useState(null);
  const [strategyFeed, setStrategyFeed] = useState({ strategies: [], financial_summary: {} });
  const [scorpComparison, setScorpComparison] = useState(null);
  const [taxDeadlines, setTaxDeadlines] = useState([]);
  const [taxAudit, setTaxAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showSavingsTimeline, setShowSavingsTimeline] = useState(false);
  const [intakeCompleted, setIntakeCompleted] = useState(true);
  const [intakeData, setIntakeData] = useState({});
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);

  // Auto-trigger: Mark dashboard as viewed
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerAction('view_dashboard');
    }, 2000); // Trigger after 2 seconds on page
    return () => clearTimeout(timer);
  }, [triggerAction]);

  useEffect(() => {
    fetchYears();
    // Check trial status for countdown banner
    axios.get(`${API}/subscription/status`, { headers: getAuthHeaders(), withCredentials: true })
      .then(r => setTrialStatus(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchData();
      fetchTaxDeadlines();
      fetchTaxAudit();
    }
  }, [selectedYear]);

  const fetchYears = async () => {
    try {
      const response = await axios.get(`${API}/transactions/years`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      // Always show at least 11 years regardless of data (current year + 10 prior years)
      const currentYear = new Date().getFullYear();
      const tenYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
      // Merge with any years that have data
      const allYears = [...new Set([...tenYears, ...response.data])].sort((a, b) => b - a);
      setAvailableYears(allYears);
      if (!allYears.includes(selectedYear)) {
        setSelectedYear(allYears[0]);
      }
    } catch (error) {
      // Default to 11 years even on error
      const currentYear = new Date().getFullYear();
      const tenYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
      setAvailableYears(tenYears);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, alertsRes, topRes, taxRes, yoyRes, strategyRes, scorpRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/spending-alerts?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/top-deductions?year=${selectedYear}&limit=5`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/tax-liability`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/year-over-year`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/dashboard/strategy-feed?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/dashboard/scorp-comparison?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data);
      setTopDeductions(topRes.data);
      setTaxLiability(taxRes.data);
      setYearOverYear(yoyRes.data);
      setStrategyFeed(strategyRes.data);
      setScorpComparison(scorpRes.data);
    } catch (error) {
      toast.error(t('toast.failed_to_load_dashboard_data'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchTaxDeadlines = async () => {
    try {
      const response = await axios.get(`${API}/strategy/tax-calendar?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setTaxDeadlines(response.data.deadlines || []);
    } catch (error) {
      // Tax deadlines unavailable
    }
  };

  const fetchTaxAudit = async () => {
    setLoadingAudit(true);
    try {
      // Check intake status in parallel with audit
      const [auditRes, intakeRes] = await Promise.all([
        axios.get(`${API}/tax-audit/quick-scan/${selectedYear}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/user/intake`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      setTaxAudit(auditRes.data);
      setIntakeCompleted(intakeRes.data.intake_completed === true);
      setIntakeData(intakeRes.data.intake_data || {});
    } catch (error) {
      setTaxAudit(null);
    } finally {
      setLoadingAudit(false);
    }
  };


  const downloadTaxLeakReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(
        `${API}/tax-audit/report/${selectedYear}`,
        { headers: getAuthHeaders(), withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SolelyTax_TaxLeakReport_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tax Leak Report downloaded!');
    } catch (error) {
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const quickActions = [
    { label: 'Add Transaction', icon: Receipt, path: '/transactions', color: 'bg-blue-500' },
    { label: 'Add Deduction', icon: Calculator, path: '/deductions', color: 'bg-emerald-500' },
    { label: 'Upload Receipt', icon: FileText, path: '/receipts', color: 'bg-purple-500' },
    { label: 'Ask AI Advisor', icon: MessageCircle, path: '/chat', color: 'bg-orange-500' },
    { label: 'Tax Strategy Report', icon: Target, path: '/strategy-advisor', color: 'bg-indigo-500' },
  ];

  const getStrategyIcon = (iconName) => {
    const icons = {
      'PiggyBank': PiggyBank,
      'Car': Car,
      'Home': Home,
      'UtensilsCrossed': UtensilsCrossed,
      'Plane': Plane,
      'Building': Building
    };
    const IconComponent = icons[iconName] || Lightbulb;
    return IconComponent;
  };

  const getOpportunityConfig = (opportunityType) => {
    const configs = {
      'home_office':             { title: 'Home Office Deduction',    Icon: Home,          bgColor: 'bg-blue-100',   iconColor: 'text-blue-600',   route: '/settings',      actionLabel: 'Set Up in Settings' },
      'mileage_tracking':        { title: 'Mileage Tracking',         Icon: Car,           bgColor: 'bg-purple-100', iconColor: 'text-purple-600', route: '/mileage',       actionLabel: 'Log Business Miles' },
      's_corp_election':         { title: 'S-Corp Election',          Icon: Building,      bgColor: 'bg-indigo-100', iconColor: 'text-indigo-600', route: '/chat',          actionLabel: 'Explore with CPA' },
      'recategorize_expenses':   { title: 'Uncategorized Expenses',   Icon: Receipt,       bgColor: 'bg-amber-100',  iconColor: 'text-amber-600',  route: '/transactions',  actionLabel: 'Review Transactions' },
      'meals_correction':        { title: 'Meals Deduction Fix',      Icon: UtensilsCrossed, bgColor: 'bg-red-100',  iconColor: 'text-red-600',    route: '/transactions',  actionLabel: 'Fix Meals Entries' },
      'health_insurance':        { title: 'Health Insurance Deduction', Icon: Zap,         bgColor: 'bg-pink-100',   iconColor: 'text-pink-600',   route: '/deductions',    actionLabel: 'Add as Deduction' },
      'retirement_contribution': { title: 'SEP-IRA / Solo 401(k)',    Icon: PiggyBank,     bgColor: 'bg-teal-100',   iconColor: 'text-teal-600',   route: '/chat',          actionLabel: 'Chat with Carlotta' },
    };
    return configs[opportunityType] || { title: 'Tax Opportunity', Icon: Zap, bgColor: 'bg-orange-100', iconColor: 'text-orange-600', route: '/strategy', actionLabel: 'Take Action' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* New Onboarding system handled by DashboardLayout */}
      
      <div className="space-y-8 pb-24" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2 font-heading text-stone-900 tracking-tight">{t('dashboard.tax_health')}</h1>
            <p className="text-stone-500 text-lg">{t('dashboard.welcome_back')}, {user?.name?.split(' ')[0]}! {t('dashboard.overview_text', { year: selectedYear })}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium whitespace-nowrap text-stone-600">{t('dashboard.tax_year')}:</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" data-testid="dashboard-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => navigate('/reports')} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all" data-testid="view-reports-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('dashboard.view_reports')}
            </Button>
          </div>
        </div>

        <SetupPreviewCard />
        <SavingsProgressTracker />
        <CpaPacketCard />

        {/* Onboarding Step Guide */}
        <StepGuide stepId="dashboard" className="mb-2" />

        {/* Feature 4: Real-time tax snapshot */}
        <TaxSnapshotWidget key={onboardingData?.progress?.updated_at} year={selectedYear} />

        {/* Feature 7: Profession-specific deduction gaps */}
        <DeductionGapsPanel year={selectedYear} />

        {/* Trial Countdown Banner */}
        {trialStatus?.plan_type === 'trial' && trialStatus.trial_days_remaining !== null && (
          <div
            className={`rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border ${
              trialStatus.trial_days_remaining <= 3
                ? 'bg-red-50 border-red-300'
                : trialStatus.trial_days_remaining <= 10
                ? 'bg-amber-50 border-amber-300'
                : 'bg-blue-50 border-blue-200'
            }`}
            data-testid="trial-countdown-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                trialStatus.trial_days_remaining <= 3 ? 'text-red-600'
                : trialStatus.trial_days_remaining <= 10 ? 'text-amber-600'
                : 'text-blue-600'
              }`} />
              <div>
                <p className={`font-semibold ${
                  trialStatus.trial_days_remaining <= 3 ? 'text-red-900'
                  : trialStatus.trial_days_remaining <= 10 ? 'text-amber-900'
                  : 'text-blue-900'
                }`}>
                  {trialStatus.trial_days_remaining === 0
                    ? 'Your free trial ends today!'
                    : `${trialStatus.trial_days_remaining} day${trialStatus.trial_days_remaining === 1 ? '' : 's'} left in your free trial`}
                </p>
                <p className="text-sm text-stone-600">
                  Subscribe to keep your data and lock in the founding member rate.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap flex-shrink-0"
              onClick={() => navigate('/pricing')}
              data-testid="trial-subscribe-btn"
            >
              Subscribe Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Intake Completion Banner — shown for users who haven't run the audit setup */}
        {!intakeCompleted && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 flex items-start gap-3" data-testid="intake-banner">
            <Zap className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-emerald-900">Your Tax Audit Setup is Incomplete</p>
              <p className="text-sm text-emerald-700 mt-1">
                Answer 5 quick questions to personalize your Tax Leak Audit — takes 2 minutes and could uncover thousands in missed savings.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/intake')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0 whitespace-nowrap"
              data-testid="intake-banner-btn"
            >
              Complete Setup
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* State Setup Reminder */}
        {user && !user.state && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-900">Set Your State for Accurate Tax Calculations</p>
              <p className="text-sm text-amber-700 mt-1">
                State income tax is not included in your estimates. Set your state in Settings to see complete tax liability.
              </p>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/settings')}
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
            >
              Set State
            </Button>
          </div>
        )}

        {/* TAX LEAK AUDIT WIDGET - P0 Feature: Tax Optimization OS */}
        {loadingAudit && (
          <Card className="border-2 border-orange-200 bg-orange-50/30 animate-pulse" data-testid="tax-leak-audit-loading">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-orange-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-orange-100 rounded w-48" />
                  <div className="h-4 bg-orange-100 rounded w-64" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="h-28 bg-orange-100 rounded-xl" />
                <div className="h-28 bg-orange-100 rounded-xl" />
                <div className="h-28 bg-orange-100 rounded-xl" />
              </div>
            </CardContent>
          </Card>
        )}

        {!loadingAudit && taxAudit && taxAudit.total_potential_savings > 0 && (
          <Card className="bg-white border-2 border-orange-300 shadow-lg overflow-hidden" data-testid="tax-leak-audit-widget">
            {/* Urgency accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />
            <CardContent className="p-6">
              {/* Header row */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shadow-sm flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-bold text-stone-900 font-heading">Tax Leak Audit</h2>
                      <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Action Required</Badge>
                    </div>
                    <p className="text-stone-500 text-sm">
                      {taxAudit.top_opportunities.length} savings {taxAudit.top_opportunities.length === 1 ? 'opportunity' : 'opportunities'} identified for {selectedYear}
                    </p>
                    {!intakeCompleted && (
                      <button
                        onClick={() => navigate('/intake')}
                        className="text-xs text-orange-600 hover:text-orange-700 underline mt-0.5"
                        data-testid="audit-improve-accuracy"
                      >
                        Complete setup to improve accuracy
                      </button>
                    )}
                  </div>
                </div>

                {/* Score + Savings headline */}
                <div className="flex items-center gap-5 bg-stone-50 rounded-xl px-5 py-3 border border-stone-200 self-start sm:self-auto">
                  <div className="text-center">
                    <div
                      className={`text-3xl font-bold font-heading ${
                        taxAudit.efficiency_score < 50 ? 'text-red-600' :
                        taxAudit.efficiency_score < 75 ? 'text-amber-600' :
                        'text-emerald-600'
                      }`}
                      data-testid="tax-efficiency-score"
                    >
                      {taxAudit.efficiency_score === 0 ? '–' : Math.round(taxAudit.efficiency_score)}
                    </div>
                    <p className="text-xs text-stone-500 font-medium whitespace-nowrap">Tax Score / 100</p>
                  </div>
                  <div className="w-px h-10 bg-stone-200 flex-shrink-0" />
                  <div className="text-center">
                    <div className="text-3xl font-bold font-heading text-orange-600" data-testid="total-potential-savings">
                      {formatCurrency(taxAudit.total_potential_savings)}
                    </div>
                    <p className="text-xs text-stone-500 font-medium whitespace-nowrap">Potential Savings/yr</p>
                  </div>
                </div>
              </div>

              {/* Opportunity Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-testid="audit-opportunities">
                {taxAudit.top_opportunities.map((opp, idx) => {
                  const cfg = getOpportunityConfig(opp.opportunity);
                  const savings = opp.potential_tax_savings || opp.annual_tax_savings || opp.potential_penalty_avoided || 0;
                  return (
                    <div
                      key={idx}
                      className="bg-stone-50 rounded-xl p-4 border border-stone-200 hover:border-orange-300 hover:bg-orange-50/40 transition-all flex flex-col"
                      data-testid={`audit-opportunity-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bgColor}`}>
                          <cfg.Icon className={`w-4 h-4 ${cfg.iconColor}`} />
                        </div>
                        <span className="font-semibold text-sm text-stone-800 leading-tight">{cfg.title}</span>
                      </div>
                      <p className="text-xs text-stone-500 mb-3 flex-1 line-clamp-3">{opp.recommendation}</p>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="text-emerald-600 font-bold text-base">{formatCurrency(savings)}</span>
                          <p className="text-xs text-stone-400">potential savings</p>
                        </div>
                        <Badge className={`text-xs ${opp.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {opp.confidence}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700"
                        onClick={() => navigate(cfg.route)}
                        data-testid={`audit-action-${idx}`}
                      >
                        {cfg.actionLabel}
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Footer CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                  onClick={() => navigate('/chat')}
                  data-testid="audit-cta-chat"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Discuss with Carlotta (AI Advisor)
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={() => setShowSavingsTimeline(true)}
                  data-testid="audit-cta-timeline"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  See 3-Year Savings Timeline
                </Button>
                <Button
                  variant="outline"
                  className="border-stone-300 text-stone-600 hover:bg-stone-50 sm:px-4"
                  onClick={downloadTaxLeakReport}
                  disabled={downloadingReport}
                  data-testid="audit-cta-download-report"
                >
                  {downloadingReport
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <FileText className="w-4 h-4 mr-2" />}
                  {downloadingReport ? 'Generating...' : 'Download Report'}
                </Button>
                <Button
                  variant="outline"
                  className="border-stone-300 text-stone-600 hover:bg-stone-50 sm:px-4"
                  onClick={() => navigate('/strategy')}
                  data-testid="audit-cta-strategy"
                >
                  <Target className="w-4 h-4 mr-2" />
                  All Strategies
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3-YEAR SAVINGS TIMELINE MODAL */}
        {taxAudit && (
          <Dialog open={showSavingsTimeline} onOpenChange={setShowSavingsTimeline}>
            <DialogContent className="max-w-2xl w-full" data-testid="savings-timeline-modal">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold font-heading text-stone-900">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Your 3-Year Tax Savings Projection
                </DialogTitle>
                <DialogDescription className="text-stone-500">
                  What you could keep over 3 years by acting on your audit findings.
                </DialogDescription>
              </DialogHeader>

              {(() => {
                // ── Commandment II: use backend-computed three_year_projection ──
                const proj = taxAudit.three_year_projection ?? {};
                const cumulative = proj.cumulative ?? 0;
                const chartData  = proj.chart_data?.map(d => ({
                  year:    d.year_label,
                  savings: d.savings,
                  lost:    d.lost ?? 0,
                })) ?? [];
                return (
                  <div className="space-y-5">
                    {/* Cumulative headline */}
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-5 text-center">
                      <p className="text-sm text-stone-500 mb-1">Total Savings Over 3 Years</p>
                      <p className="text-5xl font-bold font-heading text-emerald-600" data-testid="timeline-cumulative-savings">
                        {formatCurrency(cumulative)}
                      </p>
                      <p className="text-sm text-stone-500 mt-2">
                        That's money that could stay in your business — not go to the IRS.
                      </p>
                    </div>

                    {/* Year-by-year bar chart */}
                    <div className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={6}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value, name) => [`$${value.toLocaleString()}`, name === 'savings' ? 'Recovered' : 'Still at Risk']}
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: 12 }}
                          />
                          <Legend formatter={(v) => v === 'savings' ? 'Tax Recovered' : 'Still at Risk'} />
                          <Bar dataKey="savings" name="savings" fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="lost" name="lost" fill="#fca5a5" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Opportunity breakdown */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-stone-700">Your Top Opportunities</p>
                      {taxAudit.top_opportunities.map((opp, idx) => {
                        const cfg = getOpportunityConfig(opp.opportunity);
                        const saving = opp.potential_tax_savings || opp.annual_tax_savings || opp.potential_penalty_avoided || 0;
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-100">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cfg.bgColor}`}>
                                <cfg.Icon className={`w-3.5 h-3.5 ${cfg.iconColor}`} />
                              </div>
                              <span className="text-sm font-medium text-stone-800">{cfg.title}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-emerald-600">{formatCurrency(saving)}<span className="text-stone-400 font-normal">/yr</span></p>
                              <p className="text-xs text-stone-400">{formatCurrency(opp.three_year_projection ?? saving)} over 3 yrs</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Assumptions note */}
                    <p className="text-xs text-stone-400 border-t pt-3">
                      * Year 1 reflects ~60% adoption ramp-up. Years 2–3 project modest income growth of 5–10%. Consult a CPA for personalized projections.
                    </p>

                    {/* CTA */}
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => { setShowSavingsTimeline(false); navigate('/chat'); }}
                      data-testid="timeline-cta-chat"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Discuss Implementation with Carlotta (AI Advisor)
                    </Button>
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>
        )}

        {/* BENCHMARK VS PEERS CARD */}
        {summary?.peer_benchmark && summary.gross_income > 0 && (() => {
          // ── Commandment II: all benchmark values come from backend DTO ──
          const pb = summary.peer_benchmark;
          const pct      = pb.optimization_pct;
          const isAbove  = pb.is_above_average;
          const peerAvg  = pb.peer_avg;
          const gap      = pb.gap;
          return (
            <Card className="bg-white border border-stone-200 shadow-soft" data-testid="benchmark-card">
              <CardContent className="p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-stone-900">How You Compare to Your Peers</h3>
                      <p className="text-stone-500 text-sm">{pb.label}</p>
                    </div>
                  </div>
                  <Badge className={isAbove ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {isAbove ? 'Above Average' : 'Below Average'}
                  </Badge>
                </div>

                {/* Numbers row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">Your deductions ({selectedYear})</p>
                    <p className="text-2xl font-bold text-stone-900" data-testid="benchmark-user-total">
                      {formatCurrency(pb.user_total)}
                    </p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                    <p className="text-xs text-stone-500 mb-1">Peer average</p>
                    <p className="text-2xl font-bold text-stone-600" data-testid="benchmark-peer-avg">
                      {formatCurrency(peerAvg)}
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-stone-400 mb-1">
                    <span>$0</span>
                    <span className="font-medium">{pct}% of peer average</span>
                    <span>{formatCurrency(peerAvg)}</span>
                  </div>
                  <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${isAbove ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${pct}%` }}
                      data-testid="benchmark-progress-bar"
                    />
                  </div>
                </div>

                {/* Gap insight */}
                {!isAbove && gap > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <span className="font-bold">{formatCurrency(gap)} gap identified.</span>{' '}
                      Peers in your group typically claim {formatCurrency(peerAvg)} in deductions.
                      Closing this gap could save you an estimated{' '}
                      <span className="font-bold text-amber-900">{formatCurrency(pb.gap_tax_impact)}/yr</span> in taxes.
                    </p>
                  </div>
                )}
                {isAbove && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-emerald-800">
                      You're deducting more than average for your peer group.{' '}
                      <span className="font-bold">Run a full audit</span> to confirm there are no remaining opportunities.
                    </p>
                  </div>
                )}

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 text-sm border-stone-300 hover:bg-stone-50"
                    onClick={() => navigate('/intake')}
                    data-testid="benchmark-update-intake"
                  >
                    Update Your Profile
                  </Button>
                  <Button
                    className="flex-1 text-sm bg-stone-900 hover:bg-stone-800 text-white"
                    onClick={() => navigate('/strategy')}
                    data-testid="benchmark-close-gap"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    {isAbove ? 'View All Strategies' : 'Close the Gap'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* High Tax Liability - Chat with Carlotta CTA */}
        {summary && summary.estimated_tax_liability > 5000 && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4 flex items-start gap-3">
            <MessageCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-blue-900">Tax Liability: {formatCurrency(summary.estimated_tax_liability)}</p>
              <p className="text-sm text-blue-700 mt-1">
                Not sure how to handle this amount? Chat with Carlotta (AI Tax Advisor) for guidance on payment plans, quarterly estimates, and tax reduction strategies.
              </p>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/chat')}
              className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0 whitespace-nowrap"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat with Carlotta
            </Button>
          </div>
        )}

        {/* Estimated Tax Bill - Hero Section */}
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft hover:shadow-float transition-shadow duration-200">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div>
                <p className="text-stone-500 text-sm font-medium mb-2">{t('dashboard.estimated_tax_bill', { year: selectedYear })}</p>
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl md:text-6xl font-bold text-stone-900 font-heading tracking-tight" data-testid="estimated-tax-total">{formatCurrency(summary?.estimated_tax_liability || 0)}</span>
                  {summary?.estimated_savings > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      <TrendingDown className="w-3 h-3 mr-1" />
                      {formatCurrency(summary.estimated_savings)} {t('dashboard.saved')}
                    </Badge>
                  )}
                </div>
                <p className="text-stone-500 text-sm mt-3">
                  {t('dashboard.based_on', { income: formatCurrency(summary?.gross_income || 0), deductions: formatCurrency(summary?.total_deductions || 0) })}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 min-w-[160px]">
                  <p className="text-xs text-emerald-600 font-medium">{t('dashboard.net_income')}</p>
                  <p className="text-2xl font-bold text-emerald-700 font-heading">{formatCurrency(summary?.net_income || 0)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs text-amber-600 font-medium">{t('dashboard.remaining_to_pay')}</p>
                  <p className="text-2xl font-bold text-amber-700 font-heading">{formatCurrency(Math.max(0, summary?.remaining_tax || 0))}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax Breakdown */}
        {summary?.tax_breakdown && (
          <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft" data-testid="tax-breakdown-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-stone-900">
                <Calculator className="w-5 h-5 text-emerald-600" />
                {t('dashboard.tax_breakdown')}
              </CardTitle>
              <CardDescription className="text-stone-500">
                {t('dashboard.filing_as', { status: summary.tax_breakdown.filing_status?.replace(/_/g, ' ') })}
                {summary.tax_breakdown.state && ` • ${summary.tax_breakdown.state_info?.state_name || summary.tax_breakdown.state}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Federal Tax */}
                <div className="p-5 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-700 font-medium">{t('dashboard.federal_income_tax')}</span>
                    <Badge variant="outline" className="text-blue-600 border-blue-200">IRS</Badge>
                  </div>
                  <p className="text-2xl font-bold text-blue-800 font-heading" data-testid="federal-tax-amount">
                    {formatCurrency(summary.tax_breakdown.federal_tax || 0)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    On {formatCurrency(summary.tax_breakdown.taxable_income || 0)} taxable income
                  </p>
                </div>

                {/* State Tax */}
                <div className={`p-4 rounded-lg border ${summary.tax_breakdown.state_info?.has_income_tax ? 'bg-purple-50 border-purple-100' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${summary.tax_breakdown.state_info?.has_income_tax ? 'text-purple-700' : 'text-gray-600'}`}>
                      State Income Tax
                    </span>
                    {summary.tax_breakdown.state ? (
                      <Badge variant="outline" className={summary.tax_breakdown.state_info?.has_income_tax ? 'text-purple-600 border-purple-200' : 'text-gray-500 border-gray-300'}>
                        {summary.tax_breakdown.state}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-200">
                        Not Set
                      </Badge>
                    )}
                  </div>
                  <p className={`text-2xl font-bold ${summary.tax_breakdown.state_info?.has_income_tax ? 'text-purple-800' : 'text-gray-700'}`} data-testid="state-tax-amount">
                    {formatCurrency(summary.tax_breakdown.state_tax || 0)}
                  </p>
                  <p className={`text-xs mt-1 ${summary.tax_breakdown.state_info?.has_income_tax ? 'text-purple-600' : 'text-gray-500'}`}>
                    {summary.tax_breakdown.state_info?.note || 'Set your state in Settings'}
                  </p>
                  {!summary.tax_breakdown.state && (
                    <Button 
                      variant="link" 
                      className="text-xs p-0 h-auto mt-2 text-amber-600 hover:text-amber-700"
                      onClick={() => navigate('/settings')}
                    >
                      Set State →
                    </Button>
                  )}
                </div>

                {/* Self-Employment Tax */}
                <div className="p-5 bg-orange-50 rounded-xl border border-orange-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-orange-700 font-medium">Self-Employment Tax</span>
                    <Badge variant="outline" className="text-orange-600 border-orange-200">15.3%</Badge>
                  </div>
                  <p className="text-2xl font-bold text-orange-800 font-heading" data-testid="se-tax-amount">
                    {formatCurrency(summary.tax_breakdown.self_employment_tax || 0)}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    {formatCurrency(summary.tax_breakdown.se_deduction || 0)} deductible
                  </p>
                </div>
              </div>

              {/* W-2 Income Breakdown — shown only when W-2 wages exist */}
              {(summary.tax_breakdown.w2_wages > 0) && (
                <div className="mt-4 p-4 bg-teal-50 rounded-xl border border-teal-100" data-testid="w2-breakdown-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-teal-800">W-2 Employment Income</span>
                    <Badge variant="outline" className="text-teal-600 border-teal-200 text-xs">
                      {summary.tax_breakdown.w2_entries_count} {summary.tax_breakdown.w2_entries_count === 1 ? 'employer' : 'employers'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-teal-600 mb-0.5">W-2 Wages Included in AGI</p>
                      <p className="text-xl font-bold text-teal-900 font-heading" data-testid="w2-wages-display">
                        {formatCurrency(summary.tax_breakdown.w2_wages)}
                      </p>
                      <p className="text-xs text-teal-600 mt-0.5">
                        {summary.tax_breakdown.filing_status === 'married_filing_jointly'
                          ? 'Filer + spouse W-2 wages combined'
                          : 'Primary filer W-2 wages'}
                      </p>
                    </div>
                    {summary.tax_breakdown.w2_withholding_credit > 0 && (
                      <div>
                        <p className="text-xs text-teal-600 mb-0.5">Pre-paid via W-2 Withholding</p>
                        <p className="text-xl font-bold text-teal-900 font-heading" data-testid="w2-withholding-display">
                          −{formatCurrency(summary.tax_breakdown.w2_withholding_credit)}
                        </p>
                        <p className="text-xs text-teal-600 mt-0.5">Applied as tax credit</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="mt-6 pt-4 border-t border-stone-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">Gross Income</p>
                    <p className="font-semibold text-stone-800">{formatCurrency(summary.gross_income)}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">AGI</p>
                    <p className="font-semibold text-stone-800">{formatCurrency(summary.tax_breakdown.agi)}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Standard Deduction</p>
                    <p className="font-semibold text-stone-800">{formatCurrency(summary.tax_breakdown.standard_deduction)}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Taxable Income</p>
                    <p className="font-semibold text-stone-800">{formatCurrency(summary.tax_breakdown.taxable_income)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strategy Feed & S-Corp Optimizer Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strategy Feed */}
          <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft hover:shadow-float transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-stone-900">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Strategy Feed
              </CardTitle>
              <CardDescription className="text-stone-500">AI-powered tips based on your spending</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {strategyFeed.strategies && strategyFeed.strategies.length > 0 ? (
                <>
                  {strategyFeed.strategies.slice(0, 4).map((strategy, idx) => {
                    const IconComponent = getStrategyIcon(strategy.icon);
                    return (
                      <div 
                        key={strategy.id || idx} 
                        className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                        onClick={() => navigate('/strategy', { state: { strategyId: strategy.id } })}
                        data-testid={`strategy-feed-item-${idx}`}
                      >
                        <div className={`p-2 rounded-lg ${strategy.priority === 'high' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-stone-800">{strategy.title}</p>
                            {strategy.potential_savings > 0 && (
                              <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
                                Save {formatCurrency(strategy.potential_savings)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-stone-600 mt-1 line-clamp-2">{strategy.description}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </div>
                    );
                  })}
                  {/* Commandment II: use backend-computed total_potential_savings */}
                  {(strategyFeed.total_potential_savings ?? 0) > 0 && (
                    <div className="text-center pt-2 border-t">
                      <p className="text-sm text-emerald-600 font-medium">
                        Total potential savings: {formatCurrency(strategyFeed.total_potential_savings)}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Lightbulb className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Add transactions to get personalized tax tips</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* S-Corp Optimizer */}
          <Card className="shadow-soft border-border/40">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-500" />
                Company Structure Optimizer
              </CardTitle>
              <CardDescription>See if S-Corp could save you money</CardDescription>
            </CardHeader>
            <CardContent>
              {scorpComparison && scorpComparison.net_income > 0 ? (
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">At your current income of</p>
                    <p className="text-3xl font-bold text-gray-800">{formatCurrency(scorpComparison.net_income)}</p>
                    {scorpComparison.net_savings > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600">Switching to S-Corp could save you</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(scorpComparison.net_savings)}/year</p>
                        <p className="text-xs text-gray-500">in self-employment taxes</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-gray-600">Current SE Tax</p>
                      <p className="text-lg font-semibold text-red-600">{formatCurrency(scorpComparison.sole_prop_se_tax)}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <p className="text-gray-600">As S-Corp</p>
                      <p className="text-lg font-semibold text-emerald-600">{formatCurrency(scorpComparison.scorp_payroll_tax)}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">{scorpComparison.recommendation}</p>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={() => navigate('/chat')}
                    variant={scorpComparison.worth_it ? 'default' : 'outline'}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Ask AI to Help You Switch
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Add income to see S-Corp comparison</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Tax Deadlines */}
        {taxDeadlines.length > 0 && (
          <Card className="shadow-soft border-border/40" data-testid="tax-deadlines-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Upcoming Tax Deadlines
                </span>
                <Button variant="outline" size="sm" onClick={() => navigate('/strategy', { state: { showDeadlines: true } })}>
                  View All
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {taxDeadlines.slice(0, 3).map((deadline, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${
                      deadline.urgency === 'urgent' ? 'bg-red-50 border border-red-200' :
                      deadline.urgency === 'soon' ? 'bg-amber-50 border border-amber-200' :
                      'bg-muted/30'
                    }`}
                    onClick={() => navigate('/strategy', { state: { showDeadlines: true } })}
                    data-testid={`deadline-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        deadline.urgency === 'urgent' ? 'bg-red-100' :
                        deadline.urgency === 'soon' ? 'bg-amber-100' : 'bg-blue-100'
                      }`}>
                        <Calendar className={`w-4 h-4 ${
                          deadline.urgency === 'urgent' ? 'text-red-600' :
                          deadline.urgency === 'soon' ? 'text-amber-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{deadline.title}</p>
                        <p className="text-xs text-muted-foreground">{deadline.date}</p>
                      </div>
                    </div>
                    <Badge className={
                      deadline.urgency === 'urgent' ? 'bg-red-100 text-red-700' :
                      deadline.urgency === 'soon' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }>
                      {deadline.days_until === 0 ? 'Today!' : `${deadline.days_until} days`}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-3" data-testid="alerts-section">
            {alerts.map((alert, index) => (
              <Card 
                key={index} 
                className={`shadow-soft ${
                  alert.severity === 'warning' ? 'border-orange-300 bg-orange-50' : 'border-blue-300 bg-blue-50'
                }`}
                data-testid={`alert-${index}`}
              >
                <CardContent className="p-4 flex items-start gap-3">
                  {alert.type === 'overspending' ? (
                    <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Building className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm ${alert.severity === 'warning' ? 'text-orange-800' : 'text-blue-800'}`}>
                      {alert.message}
                    </p>
                    {alert.recommendation && (
                      <p className="text-xs text-muted-foreground mt-1">{alert.recommendation}</p>
                    )}
                  </div>
                  {alert.type === 'entity_structure' && (
                    <Button size="sm" variant="outline" onClick={() => navigate('/chat')}>
                      Learn More
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="shadow-soft border-border/40 card-hover" data-testid="stat-income">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Gross Income</span>
              </div>
              <div className="text-3xl font-bold mono text-emerald-600">{formatCurrency(summary?.gross_income)}</div>
              <p className="text-sm text-muted-foreground">{summary?.transaction_count || 0} transactions</p>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40 card-hover" data-testid="stat-expenses">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-red-600" />
                </div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Expenses</span>
              </div>
              <div className="text-3xl font-bold mono text-red-600">{formatCurrency(summary?.expenses?.total || summary?.total_deductions || 0)}</div>
              <p className="text-sm text-muted-foreground">{summary?.deduction_count || 0} deductions tracked</p>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40 card-hover" data-testid="stat-net">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Net Income</span>
              </div>
              <div className="text-3xl font-bold mono">{formatCurrency(summary?.net_income)}</div>
              <p className="text-sm text-muted-foreground">Before taxes</p>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40 card-hover" data-testid="stat-tax">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Est. Tax</span>
              </div>
              <div className="text-3xl font-bold mono text-orange-600">{formatCurrency(summary?.estimated_tax_liability)}</div>
              <p className="text-sm text-muted-foreground">~{Number(summary?.effective_tax_rate || 0).toFixed(1)}% effective rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Savings & Drafts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="shadow-soft border-border/40 bg-gradient-to-br from-emerald-50 to-lime-50" data-testid="savings-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center">
                  <PiggyBank className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-sm text-emerald-700 font-medium">Potential Tax Savings</p>
                  <p className="text-4xl font-bold text-emerald-700 mono">{formatCurrency(summary?.estimated_savings)}</p>
                  <p className="text-xs text-emerald-600">From tracked deductions{summary?.total_deductions > 0 ? ` (~${Math.round((Number(summary?.estimated_savings || 0) / Number(summary.total_deductions)) * 100)}% of expenses)` : ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {summary?.draft_transaction_count > 0 && (
            <Card className="shadow-soft border-border/40 border-orange-200 bg-orange-50" data-testid="drafts-alert-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-orange-700 font-medium">Draft Transactions</p>
                      <p className="text-4xl font-bold text-orange-700 mono">{summary?.draft_transaction_count}</p>
                      <p className="text-xs text-orange-600">Items need review before finalizing</p>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/transactions')} variant="outline">
                    Review
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* REAL-TIME TAX LIABILITY TRACKER */}
        {taxLiability && (
          <Card className="shadow-soft border border-gray-200 bg-white" data-testid="tax-liability-tracker">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Real-Time Tax Liability</h3>
                    <p className="text-sm text-gray-500">Live tracking of your {selectedYear} tax obligation</p>
                  </div>
                </div>
                {taxLiability.years?.[selectedYear] && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                    <Percent className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700 font-semibold">
                      {taxLiability.years[selectedYear]?.effective_rate || 0}% Effective Rate
                    </span>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Estimated Tax</p>
                  <p className="text-3xl font-bold text-gray-900 mono">
                    {formatCurrency(taxLiability.years?.[selectedYear]?.estimated_total_tax)}
                  </p>
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="text-orange-600 font-medium">SE Tax:</span> {formatCurrency(taxLiability.years?.[selectedYear]?.self_employment_tax)}
                    <br />
                    <span className="text-blue-600 font-medium">Income Tax:</span> {formatCurrency(taxLiability.years?.[selectedYear]?.income_tax)}
                  </div>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Quarterly Paid</p>
                  <p className="text-3xl font-bold text-emerald-600 mono">
                    {formatCurrency(taxLiability.years?.[selectedYear]?.quarterly_paid)}
                  </p>
                  <Progress 
                    value={taxLiability.years?.[selectedYear]?.payment_completion_pct ?? 0}
                    className="mt-3 h-2 bg-gray-200"
                  />
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Remaining Due</p>
                  <p className={`text-3xl font-bold mono ${
                    taxLiability.years?.[selectedYear]?.remaining_tax > 0 ? 'text-red-600' : 'text-emerald-600'
                  }`}>
                    {formatCurrency(taxLiability.years?.[selectedYear]?.remaining_tax)}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    {taxLiability.years?.[selectedYear]?.remaining_tax > 0 
                      ? 'Still owed to IRS' 
                      : 'All caught up! 🎉'}
                  </p>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-emerald-700 text-sm mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    {taxLiability.years?.[selectedYear]?.remaining_tax <= 0 ? (
                      <>
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                        <span className="text-emerald-700 font-semibold">On Track</span>
                      </>
                    ) : taxLiability.years?.[selectedYear]?.quarterly_paid > 0 ? (
                      <>
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                        <span className="text-amber-700 font-semibold">Payments Made</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-6 h-6 text-red-500" />
                        <span className="text-red-600 font-semibold">Needs Attention</span>
                      </>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => navigate('/quarterly')}
                  >
                    Manage Payments
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* YEAR-OVER-YEAR COMPARISON */}
        {yearOverYear && yearOverYear.years?.length > 0 && (
          <Card className="shadow-soft border-border/40" data-testid="year-over-year">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Year-over-Year Comparison
                </CardTitle>
                {yearOverYear.trends && Object.keys(yearOverYear.trends).length > 0 && (
                  <div className="flex items-center gap-4">
                    {yearOverYear.trends.income !== undefined && (
                      <Badge variant={yearOverYear.trends.income >= 0 ? 'default' : 'secondary'} className={yearOverYear.trends.income >= 0 ? 'bg-emerald-500' : 'bg-red-500'}>
                        {yearOverYear.trends.income >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        Income {yearOverYear.trends.income >= 0 ? '+' : ''}{yearOverYear.trends.income}%
                      </Badge>
                    )}
                    {yearOverYear.trends.expenses !== undefined && (
                      <Badge variant="secondary" className={yearOverYear.trends.expenses <= 0 ? 'bg-emerald-500 text-white' : 'bg-orange-500 text-white'}>
                        {yearOverYear.trends.expenses <= 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : <TrendingUp className="w-3 h-3 mr-1" />}
                        Expenses {yearOverYear.trends.expenses >= 0 ? '+' : ''}{yearOverYear.trends.expenses}%
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-72 mb-6" style={{ minWidth: 200, minHeight: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearOverYear.years} barGap={8}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={formatAxisMoney} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend />
                    <Bar dataKey="income" name="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="deductions" name="Deductions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* AI Insights */}
              {yearOverYear.insights?.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    AI Insights
                  </h4>
                  {yearOverYear.insights.map((insight, i) => (
                    <div 
                      key={i}
                      className={`flex items-start gap-2 p-3 rounded-lg ${
                        insight.type === 'positive' ? 'bg-emerald-50 text-emerald-800' :
                        insight.type === 'warning' ? 'bg-orange-50 text-orange-800' :
                        'bg-blue-50 text-blue-800'
                      }`}
                    >
                      {insight.type === 'positive' ? <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                       insight.type === 'warning' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                       <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                      <p className="text-sm">{insight.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Deductions Chart */}
          {topDeductions.length > 0 && (
            <Card className="shadow-soft border-border/40" data-testid="top-deductions-chart">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Top Deduction Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" style={{ minWidth: 200, minHeight: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topDeductions} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatAxisMoney} />
                      <YAxis type="category" dataKey="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar dataKey="amount" fill="#064e3b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Deadlines */}
          <Card className="shadow-soft border-border/40" data-testid="deadlines-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-500" />
                Upcoming Tax Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summary?.upcoming_deadlines?.length > 0 ? (
                <>
                  {summary.upcoming_deadlines.map((deadline, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">Q{deadline.quarter}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Q{deadline.quarter} {deadline.year}</p>
                          <p className="text-sm text-gray-500">{formatDate(deadline.due_date)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 mono">{formatCurrency(deadline.estimated_tax)}</p>
                        <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200">Due</Badge>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" className="w-full hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700" onClick={() => navigate('/quarterly')}>
                    Manage Estimates
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-gray-500">No upcoming deadlines</p>
                  <Button 
                    variant="link" 
                    className="text-emerald-600 hover:text-emerald-700"
                    onClick={() => navigate('/quarterly')}
                  >
                    Add quarterly estimate
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="shadow-soft border-border/40" data-testid="quick-actions">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto py-6 flex flex-col gap-3 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all"
                  onClick={() => navigate(action.path)}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium">{action.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Advisor Promo */}
        <Card className="bg-white border border-gray-200 shadow-lg" data-testid="ai-promo">
          <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <img 
              src="https://images.unsplash.com/photo-1758599543125-0a927f1d7a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMGdsYXNzZXMlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NjU2NTUzODl8MA&ixlib=rb-4.1.0&q=85&w=200"
              alt="Carlotta Thompson"
              className="w-20 h-20 rounded-full object-cover border-4 border-emerald-400"
            />
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Need Tax Strategy Advice?</h3>
              <p className="text-gray-600 mb-4">
                Chat with Carlotta Thompson, your AI tax strategist. Get personalized recommendations 
                for maximizing deductions, S-Corp election guidance, and entity structuring advice.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => navigate('/chat')}
                  data-testid="chat-with-carlotta-btn"
                >
                  Chat with Carlotta
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate('/reports')}
                >
                  Generate Tax Package
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
